/**
 * Module-level notifier bridge.
 *
 * notistack (v1 and v2 alike) only exposes `enqueueSnackbar` through the
 * `useSnackbar` hook, so code outside React (React Query caches, interceptors,
 * stores) cannot toast directly. A tiny bridge component inside `<SnackbarProvider>` registers the
 * real alert function here at runtime; everything else calls `notify(...)`.
 */

export type AlertSeverity = 'error' | 'success' | 'warning' | 'info';

export type NotifyFn = (
  message: string,
  severity: AlertSeverity,
  timeOut?: number
) => void;

let notifier: NotifyFn | null = null;

/** Register the live alert function (called by the in-app bridge). */
export const registerNotifier = (fn: NotifyFn | null): void => {
  notifier = fn;
};

/**
 * Surface a message through the app's single toast surface.
 * Falls back to console when called before the provider mounts.
 */
export const notify = (
  message: string,
  severity: AlertSeverity = 'error',
  timeOut?: number
): void => {
  if (notifier) {
    notifier(message, severity, timeOut);
    return;
  }
  // eslint-disable-next-line no-console
  console.error(`[notify:${severity}] ${message}`);
};
